import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

import { useGetBeneficiaryDepositDetailsQuery } from '@/api/v1';
import CopyIcon from '@/assets/icons/copy.svg?react';
import MxnbIcon from '@/assets/icons/mxnb.svg?react';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly beneficiaryId?: string;
}

const NOT_READY_ERROR_CODE = 'beneficiary.deposit-details-not-ready';

const isNotReadyError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const fetchError = error as { status?: number; data?: { errorCode?: string } };
  return fetchError.status === 409 && fetchError.data?.errorCode === NOT_READY_ERROR_CODE;
};

export const MxnDepositDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const { beneficiaryId } = props;
  const tr = useTranslator();
  const toast = useToast();

  const { data, error, isFetching } = useGetBeneficiaryDepositDetailsQuery(
    {
      beneficiaryId: beneficiaryId ?? '',
      currency: CurrencyCodeType.MXN,
    },
    {
      skip: !beneficiaryId,
    },
  );

  const clabe = data?.accountDetails?.clabe ?? '';

  const copyClabe = React.useCallback(async (): Promise<void> => {
    if (!clabe) {
      return;
    }
    try {
      await navigator.clipboard.writeText(clabe);
      toast.show(tr('SPEI_CLABE_COPIED'), 'success');
    } catch {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [clabe, toast, tr]);

  if (isFetching) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('LOADING_PLEASE_WAIT')}&hellip;
        </Typography>
      </Box>
    );
  }

  if (isNotReadyError(error)) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('MXN_DEPOSIT_DETAILS_PROVISIONING')}
        </Typography>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('ACCOUNT_INFORMATION_ERROR')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <MxnbIcon width={24} height={24} />
        <Typography variant="body2" color="text.primary">
          {CurrencyCodeType.MXNB}
        </Typography>
      </Stack>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label={tr('SPEI_BANK_NAME')}
          value={data.bankName}
          fullWidth={true}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={tr('SPEI_BANK_BENEFICIARY')}
          value={data.beneficiaryName}
          fullWidth={true}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label="CLABE"
          value={clabe}
          fullWidth={true}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton color="inherit" onClick={copyClabe} disabled={!clabe}>
                    <CopyIcon fill="currentColor" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    </Box>
  );
};

const styles: Record<string, any> = {
  container: {
    mt: 1,
    px: 4,
    py: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
  },
};
